import React, { ComponentProps } from "react";

import { useDefinition } from "../../../hooks/useDefinition";
import { useFeatureFlagToggle } from "../../../hooks/useFeatureFlags";
import { useLabels } from "../../../hooks/useLabels";
import { showDialogAsync } from "../../dialog-queue";
import { showErrorDialogAsync } from "../error-dialog";
import { showLoginDialogAsync } from "../login-dialog";
import { showSaveDialogConfirmAsync } from "../save-dialog-confirm";
import { labels } from "./labels";
import { SaveDialog } from "./save-dialog";

const SaveDialogContainer = ({
	isLeavingPage,
	...dialogProps
}: {
	isLeavingPage?: boolean;
} & Pick<
	ComponentProps<typeof SaveDialog>,
	"onDontSave" | "isOpen" | "onSave" | "onScrimClick" | "onBackToProduct"
>) => {
	const [[save, cancel, title, message], areLabelsLoading] = useLabels(
		isLeavingPage
			? [labels.leave.save, labels.leave.cancel, labels.leave.title, labels.leave.message]
			: [labels.save.save, labels.save.cancel, labels.save.title, labels.save.message],
	);

	const showFullScreenFormDialogs = useFeatureFlagToggle("fullScreenFormDialogs");

	const definition = useDefinition();

	if (!definition) {
		return null;
	}

	const { maxProjectNameLength } = definition.product;
	const projectName = (definition.name ? definition.name : definition.product.name).substring(
		0,
		maxProjectNameLength,
	);

	return areLabelsLoading ? null : (
		<SaveDialog
			title={title}
			dontSave={cancel}
			save={save}
			initialName={projectName}
			nameMaxLength={maxProjectNameLength}
			messageHtml={message}
			fullScreen={showFullScreenFormDialogs}
			{...dialogProps}
		/>
	);
};

const showSaveDialogAsync = (isLeavingPage?: boolean) =>
	showDialogAsync<string | false | undefined>(({ resolve }, { isOpen }) => (
		<SaveDialogContainer
			onDontSave={() => resolve(false)}
			onSave={resolve}
			onScrimClick={() => resolve(undefined)}
			isOpen={isOpen}
			isLeavingPage={isLeavingPage}
			onBackToProduct={() => resolve(undefined)}
		/>
	));

export const showSaveAndConfirmDialogAsync = ({
	editor,
	busyBlocker,
	getIsSignedIn,
	isLeavingPage,
}: {
	editor: eXpress.core.IEditor;
	busyBlocker: eXpress.IBalancedScope;
	getIsSignedIn: () => boolean;
	isLeavingPage?: boolean;
}) => {
	const confirmAsync = (projectName: string) =>
		busyBlocker.during(editor.projectNameExistsAsync(projectName)).then((projectNameExists) => {
			if (projectNameExists) {
				return showSaveDialogConfirmAsync(projectName);
			}

			return Promise.resolve(true);
		});

	const assureSignedInUserAsync: () => Promise<boolean | undefined> = () =>
		getIsSignedIn()
			? Promise.resolve(true)
			: showLoginDialogAsync(eXpress.creator.LoginFlow.SaveProject, {
					editor,
			  });

	const askNameAsync: () => Promise<string | boolean | undefined> = async () =>
		// eslint-disable-next-line @typescript-eslint/no-unsafe-return
		showSaveDialogAsync(isLeavingPage).then(async (saveResult) => {
			if (saveResult) {
				// eslint-disable-next-line @typescript-eslint/no-unsafe-return
				return assureSignedInUserAsync().then((isUserSignedIn) =>
					isUserSignedIn
						? confirmAsync(saveResult).then((shouldOverwrite) =>
								shouldOverwrite
									? busyBlocker.during(
											editor
												.saveDefinitionAsync(saveResult)
												.catch(async (e: unknown) => {
													await showErrorDialogAsync({
														errorMessageLabel: labels.save.error,
													});

													throw e;
												})
												.then(() => true),
									  )
									: askNameAsync(),
						  )
						: undefined,
				);
			}

			// eslint-disable-next-line @typescript-eslint/no-unsafe-return
			return busyBlocker.during(editor.saveDefinitionAsActiveAsync().then(() => saveResult));
		});

	return askNameAsync();
};
